import endpoints from '@/constants/endpoints';
import { ProductState } from '@/hooks/product/catalog/useGetProducts/productState';
import { ITEMS_PAGE_LIMIT } from '@/utils/constants';
import { DashboardUserType } from '@/utils/enums';
import { getSessionData } from '@/utils/session/sessionManager';
import axiosV2 from '../axiosv2';
import {
  ProductCatalogResponse,
  ProductOperationResponse,
  UpdateProductRequest,
} from '../model/product/productDto';

export const getProducts = async (args: {
  pageNumber?: number;
  searchTerm?: string;
  sellerIds?: string[];
  state?: ProductState;
}): Promise<ProductCatalogResponse> => {
  const endpoint = getEndpoint({
    adminEndpoint: endpoints.productCatalogV2.list,
    sellerEndpoint: endpoints.seller.product.list,
  });

  const filter = (() => {
    if (!args.searchTerm) return;
    const session = getSessionData();
    if (!session) throw 'Session not found';
    switch (session.type) {
      case DashboardUserType.ADMIN:
        return { filter: args.searchTerm };
      case DashboardUserType.SELLER:
        return { query: args.searchTerm };
    }
  })();

  const sellerIdsFilter = args.sellerIds?.join(',').trim();

  const response = await axiosV2.get<ProductCatalogResponse>(endpoint, {
    params: {
      limit: ITEMS_PAGE_LIMIT,
      ...(args.pageNumber && { page: `${args.pageNumber}` }),
      ...(filter && { ...filter }),
      ...(args.state && { status: `${args.state}` }),
      ...(sellerIdsFilter && { seller_id: `${sellerIdsFilter}` }),
    },
  });
  if (response.data === null) response.data = [];

  return response;
};

export const enableProduct = async (
  productId: string,
  productSku: string
): Promise<void> => {
  const endpoint = getEndpoint({
    adminEndpoint: endpoints.productCatalogV2.enable,
    sellerEndpoint: endpoints.seller.product.enable,
  });

  const response = await axiosV2.patch<ProductOperationResponse>(endpoint, [
    {
      product_id: productId,
      sku_id: productSku,
    },
  ]);

  if (response.failed_products?.length)
    throw response.failed_products[0].error ?? 'error';
  return;
};

export const disableProduct = async (
  productId: string,
  productSku: string
): Promise<void> => {
  const endpoint = getEndpoint({
    adminEndpoint: endpoints.productCatalogV2.disable,
    sellerEndpoint: endpoints.seller.product.disable,
  });

  const response = await axiosV2.patch<ProductOperationResponse>(endpoint, [
    {
      product_id: productId,
      sku_id: productSku,
    },
  ]);
  if (response.failed_products?.length)
    throw response.failed_products[0].error ?? 'error';
  return;
};

export const deleteProduct = async (
  productId: string,
  productSku: string
): Promise<void> => {
  const endpoint = getEndpoint({
    adminEndpoint: endpoints.productCatalogV2.delete,
    sellerEndpoint: endpoints.seller.product.delete,
  });

  const response = await axiosV2.delete<ProductOperationResponse>(endpoint, {
    data: [
      {
        product_id: productId,
        sku_id: productSku,
      },
    ],
  });
  if (response.failed_products?.length)
    throw response.failed_products[0].error ?? 'error';
  return;
};

export const updatePrice = async (
  updateProductRequest: UpdateProductRequest
): Promise<void> => {
  const endpoint = getEndpoint({
    adminEndpoint: endpoints.productCatalogV2.update,
    sellerEndpoint: endpoints.seller.product.update,
  });
  await axiosV2.patch(endpoint, updateProductRequest);
};

export const updateImage = async (args: {
  productId: string;
  skuId: string;
  file: File;
}): Promise<void> => {
  const endpoint = endpoints.productCatalogV2.updateImage;
  await axiosV2.patchForm(endpoint, {
    product_id: args.productId,
    sku_id: args.skuId,
    file: args.file,
  });
};

const getEndpoint = (args: {
  adminEndpoint: string;
  sellerEndpoint: string;
}) => {
  const session = getSessionData();
  if (!session) throw Error('user invalid');
  switch (session.type) {
    case DashboardUserType.ADMIN:
      return args.adminEndpoint;
    case DashboardUserType.SELLER:
      return args.sellerEndpoint;
  }
};
